import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Trophy, ShieldCheck, User, Crown } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = {
  owner: { label: "Mister", icon: Crown, color: "text-amber-600" },
  director: { label: "Dirigente", icon: ShieldCheck, color: "text-sky-600" },
  parent: { label: "Genitore", icon: User, color: "text-emerald-600" },
};

export default function AcceptInvitePage() {
  const { token } = useParams();
  const { user } = useAuth();
  const { refresh, setActive } = useTeams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/${token}`);
        setInvite(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Invito non valido");
      }
    })();
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/invites/accept`, { token });
      await refresh();
      setActive(data.team_id);
      toast.success("Sei entrato nella squadra!");
      navigate("/app");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile accettare");
    } finally {
      setAccepting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <Card className="p-8 max-w-md w-full text-center border-slate-200">
          <h1 className="font-heading text-2xl font-bold mb-2">Invito non valido</h1>
          <p className="text-slate-500 mb-6">{error}</p>
          <Link to="/app">
            <Button>Torna alla home</Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        Caricamento invito...
      </div>
    );
  }

  const Role = ROLE_LABEL[invite.role] || ROLE_LABEL.director;
  const RoleIcon = Role.icon;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <Card className="p-8 max-w-md w-full border-slate-200">
        <div className="flex items-center justify-center mb-6">
          <div className="h-14 w-14 rounded-2xl bg-sky-500 flex items-center justify-center">
            <Trophy size={26} className="text-white" />
          </div>
        </div>
        <h1 className="font-heading text-3xl font-bold text-center mb-2">
          Sei stato invitato!
        </h1>
        <p className="text-slate-600 text-center mb-8">
          Sei stato invitato a unirti alla squadra
        </p>

        <div className="space-y-4 mb-8">
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-center">
            <div className="label-eyebrow mb-1">Squadra</div>
            <div className="font-heading font-semibold text-lg">{invite.team?.name}</div>
            <div className="text-sm text-slate-500">{invite.team?.category}</div>
          </div>
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center gap-2">
            <RoleIcon size={18} className={Role.color} />
            <span className="font-medium">Ruolo: {Role.label}</span>
          </div>
          {invite.player && (
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-center">
              <div className="label-eyebrow mb-1">Giocatore associato</div>
              <div className="font-medium">
                {invite.player.first_name} {invite.player.last_name}
              </div>
            </div>
          )}
        </div>

        {user && user !== false ? (
          <Button
            onClick={accept}
            disabled={accepting}
            className="w-full rounded-full bg-sky-500 hover:bg-sky-600"
            data-testid="accept-invite-btn"
          >
            {accepting ? "Accettazione..." : "Accetta invito"}
          </Button>
        ) : (
          <div className="space-y-3">
            <p className="text-center text-sm text-slate-500">
              Per accettare devi prima accedere o registrarti.
            </p>
            <Link to={`/login?redirect=/invito/${token}`}>
              <Button className="w-full rounded-full bg-sky-500 hover:bg-sky-600">
                Accedi / Registrati
              </Button>
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}
